import { getFlattenedNodes, SummaryType } from '@ls/grouping-service';
import { memoize, uniqueByKey } from '@ls/js-utils';
import { createSelector } from 'reselect';
import CalcType from 'state/Constants/CalcType';
import SelectionState from 'state/selections/SelectionState';
import { selectSelectedAccountEntityId, selectSelectedBenchmarkEntityId, selectSelectedAccountEntity } from 'state/selections/selectors';
import { getEntitySummariesObject, getEntitySummariesObjectForBench } from 'state/util';
import scope from './scope';

export const selectTraceAnalyticValues = state => state[scope]['traceAnalyticValues'];
export const selectFetchingTraceAnalyticValues = state => state[scope]['fetchingTraceAnalyticValues'];
export const selectTracePeriodValues = state => state[scope]['tracePeriodValues'];
export const selectFetchingTracePeriodValues = state => state[scope]['fetchingTracePeriodValues'];
export const selectTraceTypeOptions = state => state[scope]['traceTypeOptions'];
export const selectGroupingResponse = (summaryType) => state => state[scope]['gridData'][summaryType];
export const selectFetchingGridData = (summaryType) => state => state[scope]['fetchingGridData'][summaryType];

export const selectTraceAnalyticOptions = createSelector(
  selectTraceAnalyticValues,
  values => {
    return values?.map(v => {
      return {
        label: v.split('_')
          .map(vPartial => {
            const vPartialLower = vPartial.toLowerCase();
            return vPartialLower[0].toUpperCase() + vPartialLower.slice(1);
          })
          .join(' '),
        value: v,
      };
    }) ?? [];
  },
);

export const selectTracePeriodOptions = createSelector(
  selectTracePeriodValues,
  values => values?.map(v => ({
    label: Number(v?.split('_')?.[1]),
    value: v,
  })) ?? [],
);

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return state => state[scope]['definitions'][selectionState];
  }

  return createSelector(
    state => state[scope]['definitions'][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(({ complete }) => complete);
      return completeDefinitions.filter(uniqueByKey(completeDefinitions, 'key'));
    },
  );
});

export const selectFlattenedNodes = memoize((summaryType) => {
  return createSelector(
    selectGroupingResponse(summaryType),
    (groupingResponse) => getFlattenedNodes(groupingResponse?.root) ?? [],
  );
});

const toTracePrimaryKeyString = (securityId, tracePeriod) => `${securityId}_${tracePeriod}`
export const selectGlobalTraceMap = memoize((summaryType) => {
  return createSelector(
    selectGroupingResponse(summaryType),
    (groupingResponse) => groupingResponse?.tracePricingSecurities?.reduce((map, tracePricingSecurity) => {
      const primaryKeyText = toTracePrimaryKeyString(tracePricingSecurity.primaryKey.securityId, tracePricingSecurity.primaryKey.period);
      map[primaryKeyText] = tracePricingSecurity;
      return map;
    }, {}),
  );
});

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectFlattenedNodes(SummaryType.ABSOLUTE),
  selectFlattenedNodes(SummaryType.RELATIVE),
  selectGlobalTraceMap(SummaryType.ABSOLUTE),
  selectGlobalTraceMap(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absNodes, relNodes, absTraceMap, relTraceMap, accEntityId, bmkEntityId, benchId) => {
    const rowMap = {};
    const allNodes = [...absNodes, ...relNodes];
    allNodes.forEach(node => {
      const { pathString } = node;
      if (!rowMap[pathString]) {
        rowMap[pathString] = { pathString };
      }
      const row = rowMap[pathString];

      if (node.securityId && definitions?.length) {
        definitions
          .filter(({ key }) => !row[key])
          .forEach((definition) => {
            let value;
            if (definition.calcType === CalcType.Relative) {
              value = relTraceMap?.[toTracePrimaryKeyString(node.securityId, definition.tracePeriod)]?.[definition.traceAnalyticGroupingField];
            } else {
              value = absTraceMap?.[toTracePrimaryKeyString(node.securityId, definition.tracePeriod)]?.[definition.traceAnalyticGroupingField];
            }
            row[definition.key] = value;
          });
      } else {
        definitions
          .filter(({ key }) => !row[key])
          .forEach((definition) => {
            row[definition.key] = findTraceValue(
              node,
              definition,
              accEntityId,
              bmkEntityId,
              benchId?.benchmarkId,
            );
          });
      }
    });
    return rowMap;
  },
);

export const findTraceValue = (node, definition, accEntityId, bmkEntityId, benchId) => {
  const { traceAnalyticGroupingField, tracePeriod, calcType } = definition;
  const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
  const benchValue = calcType === CalcType.Benchmark ? 0 : benchId;
  const isRelative = calcType === CalcType.Relative;
  let entitySummariesObject;
  if (benchValue !== null && benchValue !== undefined) {
    entitySummariesObject = getEntitySummariesObjectForBench(node, entityId, benchValue, isRelative);
  } else {
    entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};
  }

  const tracePricingSummary = entitySummariesObject
    ?.tracePricingSummaries
    ?.find(summary => summary?.primaryKey?.period === tracePeriod) ?? {};
  return tracePricingSummary[traceAnalyticGroupingField];
};